/**
 * Price Display Component
 * Formatted price with currency
 */

import React from 'react';
import styled from 'styled-components';

interface PriceDisplayProps {
  price: number;
  originalPrice?: number | null;
  currency?: string;
  currencySymbol?: string;
  size?: 'small' | 'medium' | 'large';
  showDiscount?: boolean;
}

const PriceDisplay: React.FC<PriceDisplayProps> = ({
  price,
  originalPrice,
  currency = 'USD',
  currencySymbol = '$',
  size = 'medium',
  showDiscount = true
}) => {
  const formatPrice = (value: number) => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const hasDiscount = originalPrice && originalPrice > price;
  const discountPercent = hasDiscount
    ? Math.round(((originalPrice - price) / originalPrice) * 100)
    : 0;

  return (
    <Container $size={size}>
      <CurrentPrice $size={size}>
        {currencySymbol}{formatPrice(price)}
      </CurrentPrice>

      {hasDiscount && (
        <>
          <OriginalPrice $size={size}>
            {currencySymbol}{formatPrice(originalPrice)}
          </OriginalPrice>

          {showDiscount && (
            <DiscountBadge $size={size}>
              -{discountPercent}%
            </DiscountBadge>
          )}
        </>
      )}
    </Container>
  );
};

const Container = styled.div<{ $size: string }>`
  display: flex;
  align-items: center;
  gap: ${props => props.$size === 'small' ? '4px' : '8px'};
  flex-wrap: wrap;
`;

const CurrentPrice = styled.span<{ $size: string }>`
  font-size: ${props => {
    switch (props.$size) {
      case 'small': return '14px';
      case 'large': return '24px';
      default: return '18px';
    }
  }};
  font-weight: 600;
  color: var(--tg-theme-text-color, #000000);
`;

const OriginalPrice = styled.span<{ $size: string }>`
  font-size: ${props => {
    switch (props.$size) {
      case 'small': return '11px';
      case 'large': return '16px';
      default: return '14px';
    }
  }};
  color: var(--tg-theme-hint-color, #999);
  text-decoration: line-through;
`;

const DiscountBadge = styled.span<{ $size: string }>`
  background: #f44336;
  color: white;
  font-size: ${props => props.$size === 'small' ? '9px' : '11px'};
  font-weight: 600;
  padding: ${props => props.$size === 'small' ? '2px 4px' : '3px 6px'};
  border-radius: 4px;
`;

export default PriceDisplay;
